import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useMeta, useSnapshot } from "@/lib/AppContext";
import { StatusChip, Empty } from "@/components/chips";
import * as Icons from "lucide-react";

const val = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

function RefChip({ r }) {
  const { sid } = useParams();
  const clickable = r.resolved && r.fub_id;
  const named = r.name || r.by_name;
  const label = named || `Unresolved ${r.label || "ref"} (ID ${r.fub_id})`;
  const inner = (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border ${clickable ? "border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100" : "border-stone-300 bg-stone-100 text-stone-500"}`}
      title={clickable ? "" : "Unresolved reference — raw id preserved"}
    >
      {!clickable && <Icons.HelpCircle className="w-3 h-3" />}
      {r.label && named && <span className="text-stone-400">{r.label}:</span>} {label}
    </span>
  );
  return clickable ? (
    <Link to={`/s/${sid}/asset/${r.type}/${r.fub_id}`} data-testid={`ref-${r.type}-${r.fub_id}`}>{inner}</Link>
  ) : inner;
}

const isRefList = (v) =>
  Array.isArray(v) && v.length > 0 && v.every((x) => x && typeof x === "object" && "type" in x && "fub_id" in x);

function KeyValues({ obj, skip = [] }) {
  const entries = Object.entries(obj || {}).filter(([k]) => !skip.includes(k));
  if (!entries.length) return null;
  return (
    <dl className="grid sm:grid-cols-[200px_1fr] gap-x-4 gap-y-2 text-sm">
      {entries.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="text-stone-500">{k}</dt>
          <dd className="text-stone-900 break-words">
            {isRefList(v)
              ? <span className="flex flex-wrap gap-1.5">{v.map((r, i) => <RefChip key={i} r={r} />)}</span>
              : val(v)}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-5 mb-5">
      {title && <h3 className="text-sm font-semibold text-stone-700 uppercase tracking-wide mb-3">{title}</h3>}
      {children}
    </div>
  );
}

function EmailBody({ d }) {
  return (
    <Card title="Email">
      <div className="text-sm text-stone-500">Subject</div>
      <div className="text-lg text-stone-900 mb-4">{d.Subject || "—"}</div>
      <div className="text-sm text-stone-500 mb-2">Rendered content</div>
      <iframe title="email-body" sandbox="" data-testid="email-body-frame"
        className="w-full h-[520px] border border-stone-200 rounded-lg bg-white"
        srcDoc={d.BodyHtml || "<p style='font-family:sans-serif;color:#888'>(empty)</p>"} />
    </Card>
  );
}

function Steps({ steps, kind }) {
  if (!steps?.length) return <Empty>No steps captured.</Empty>;
  return (
    <ol className="space-y-2">
      {steps.map((s, i) => (
        <li key={i} data-testid={`step-${i}`} className="flex gap-3 items-start" style={{ marginLeft: (s.depth || 0) * 20 }}>
          <span className="mono text-xs text-stone-400 pt-1 w-14 shrink-0">{kind === "automation" ? s.kind : s.when}</span>
          <div className="flex-1 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
            <div className="text-sm text-stone-900">{s.text}</div>
            {s.refs?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">{s.refs.map((r, j) => <RefChip key={j} r={r} />)}</div>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

function SmartCriteria({ d }) {
  if (!d["Criteria available"]) {
    return (
      <Card title="Filter criteria">
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="no-criteria-note">
          {d["Legacy note"] || "Filter criteria were not captured for this Smart List."}
        </div>
      </Card>
    );
  }
  return (
    <Card title="Filter criteria">
      {(d.Criteria || []).map((group, gi) => (
        <div key={gi} className="mb-3">
          {gi > 0 && <div className="text-xs text-stone-400 my-2">— OR —</div>}
          <div className="space-y-1.5">
            {group.map((c, ci) => (
              <div key={ci} className="flex items-center gap-2 text-sm flex-wrap">
                {ci > 0 && <span className="text-xs text-stone-400">AND</span>}
                <span className="font-medium text-stone-800">{c.field}</span>
                <span className="text-stone-500">{c.operator}</span>
                <span className="text-stone-900">{(c.values || []).join(", ") || "—"}</span>
                {!c.resolved && <Icons.HelpCircle className="w-3.5 h-3.5 text-stone-400" />}
              </div>
            ))}
          </div>
        </div>
      ))}
    </Card>
  );
}

function LeadFlowBody({ d }) {
  return (
    <>
      <Card title="Source">
        <KeyValues obj={{ Source: d.Source, "Source id": d["Source id"], "Lead type": d["Lead type"], Archived: d.Archived }} />
      </Card>
      {(d.Routes || []).map((rt, i) => (
        <Card key={i} title={rt.default ? "Default route" : `Advanced route #${rt.Sort ?? i}`}>
          <KeyValues obj={rt} skip={["default", "Initial text", "Conditions"]} />
          {rt["Initial text"] && (
            <div className="mt-3">
              <div className="text-xs text-stone-500 mb-1">Initial text</div>
              <div className="text-sm bg-stone-50 border border-stone-200 rounded-lg p-3">{rt["Initial text"]}</div>
            </div>
          )}
          {rt.Conditions && (
            <div className="mt-3">
              <div className="text-xs text-stone-500 mb-1">Advanced rules</div>
              <pre className="mono text-xs bg-stone-50 border border-stone-200 rounded-lg p-3 overflow-auto">{JSON.stringify(rt.Conditions, null, 2)}</pre>
            </div>
          )}
        </Card>
      ))}
    </>
  );
}

function Body({ type, d }) {
  if (type === "email_templates") return <EmailBody d={d} />;
  if (type === "text_templates")
    return <Card title="Message"><div className="text-sm bg-stone-50 border border-stone-200 rounded-lg p-4 whitespace-pre-wrap">{d.Message || "—"}</div></Card>;
  if (type === "action_plans")
    return (
      <>
        <Card title="Overview">
          <KeyValues obj={d} skip={["Steps", "Initial text"]} />
          {d["Initial text"] && <div className="mt-3 text-sm bg-stone-50 border border-stone-200 rounded-lg p-3">{d["Initial text"]}</div>}
        </Card>
        <Card title="Steps"><Steps steps={d.Steps} kind="plan" /></Card>
      </>
    );
  if (type === "automations")
    return (
      <>
        <Card title="Overview"><KeyValues obj={d} skip={["Steps"]} /></Card>
        <Card title="Steps (in order)"><Steps steps={d.Steps} kind="automation" /></Card>
      </>
    );
  if (type === "smart_lists")
    return (
      <>
        <Card title="Overview"><KeyValues obj={d} skip={["Criteria", "Criteria available", "Legacy note", "Legacy list"]} /></Card>
        <SmartCriteria d={d} />
      </>
    );
  if (type === "lead_flows") return <LeadFlowBody d={d} />;
  return <Card title="Details"><KeyValues obj={d} /></Card>;
}

export default function AssetPage() {
  const { sid, type, id } = useParams();
  const { meta } = useMeta();
  const { reload } = useSnapshot();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [status, setStatus] = useState("Unreviewed");
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setA(null);
    api.get(`/snapshots/${sid}/assets/${type}/${id}`).then((r) => {
      setA(r.data);
      setStatus(r.data.review?.status || "Unreviewed");
      setNote(r.data.review?.note || "");
    });
  }, [type, id, sid]);

  const save = async () => {
    await api.put(`/snapshots/${sid}/reviews/${type}/${id}`, { status, note });
    setSaved(true);
    reload && reload();
    setTimeout(() => setSaved(false), 1500);
  };

  const resetReview = async () => {
    await api.delete(`/snapshots/${sid}/reviews/${type}/${id}`);
    setStatus("Unreviewed"); setNote("");
    setSaved(true); reload && reload();
    setTimeout(() => setSaved(false), 1200);
  };

  if (!a) return <div className="p-10 text-stone-400">Loading…</div>;
  const d = a.display || {};

  return (
    <div className="p-8 max-w-5xl" data-testid="asset-detail">
      <button onClick={() => nav(-1)} className="text-sm text-stone-500 hover:text-stone-800 flex items-center gap-1 mb-3" data-testid="back-btn">
        <Icons.ChevronLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-xs uppercase tracking-wider text-stone-400">{meta?.type_labels?.[type]}</div>
          <h1 className="font-display text-3xl text-stone-900 mt-1">{a.name}</h1>
          <div className="mt-2 flex items-center gap-2">
            {a.badges?.status && <StatusChip value={a.badges.status} />}
            <span className="mono text-xs text-stone-400">ID {a.fub_id}</span>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_300px] gap-6">
        <div>
          {a.folders?.length > 0 && (
            <Card title="Folders">
              <div className="flex flex-wrap gap-2">
                {a.folders.map((f) => (
                  <span key={f.id} data-testid={`folder-${f.id}`} className="text-xs px-2 py-1 rounded-md border border-stone-300 bg-stone-100 text-stone-700">{f.name}</span>
                ))}
              </div>
            </Card>
          )}
          <Body type={type} d={d} />

          {a.references?.length > 0 && (
            <Card title="References">
              <div className="flex flex-wrap gap-2">{a.references.map((r, i) => <RefChip key={i} r={r} />)}</div>
            </Card>
          )}
          {a.used_by?.length > 0 && (
            <Card title="Used by">
              <div className="flex flex-wrap gap-2">{a.used_by.map((u, i) => <RefChip key={i} r={{ ...u, resolved: true }} />)}</div>
            </Card>
          )}
        </div>

        <div className="lg:sticky lg:top-6 self-start w-full">
          <Card title="Cleanup review (local only)">
            <p className="text-xs text-stone-400 mb-3">Local to this snapshot. Never sends anything to Follow Up Boss.</p>
            <div className="space-y-2" data-testid="review-controls">
              {meta?.review_states?.map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="review" checked={status === s} onChange={() => setStatus(s)}
                    data-testid={`review-radio-${s.replace(/\s+/g, "-").toLowerCase()}`} />
                  {s}
                </label>
              ))}
            </div>
            <textarea data-testid="review-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Short note…"
              className="w-full mt-3 border border-stone-300 rounded-lg text-sm p-2 h-24 focus:outline-none focus:ring-2 focus:ring-amber-400" />
            <button onClick={save} data-testid="save-review-btn" className="mt-3 w-full bg-stone-900 text-white text-sm py-2 rounded-lg hover:bg-stone-700 transition-colors">
              {saved ? "Saved ✓" : "Save review"}
            </button>
            <div className="flex gap-2 mt-2">
              <button onClick={() => setNote("")} data-testid="clear-note-btn" className="flex-1 border border-stone-300 text-stone-600 text-xs py-1.5 rounded-lg hover:bg-stone-100">Clear note</button>
              <button onClick={resetReview} data-testid="reset-review-btn" className="flex-1 border border-stone-300 text-stone-600 text-xs py-1.5 rounded-lg hover:bg-stone-100">Reset to Unreviewed</button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
